import React, { useEffect, useState, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Spinner,
  Alert,
  Button,
} from "react-bootstrap";
import { FaHeart, FaStar, FaStarHalfAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { formatRupiah } from "../utils/formatRupiah";
import { useTranslation } from "react-i18next";
import "../css/Favorite.css";

const RatingStars = ({ ratingValue }) => {
  const fullStars = Math.floor(ratingValue);
  const halfStar = ratingValue - fullStars >= 0.5;
  const emptyStars = 5 - fullStars - (halfStar ? 1 : 0);

  return (
    <div className="rating-stars d-flex mb-2">
      {Array.from({ length: fullStars }).map((_, i) => (
        <FaStar key={`full-${i}`} className="text-warning" />
      ))}
      {halfStar && <FaStarHalfAlt key="half" className="text-warning" />}
      {Array.from({ length: emptyStars }).map((_, i) => (
        <FaStar key={`empty-${i}`} className="text-muted" />
      ))}
    </div>
  );
};

function Favorite() {
  const { t } = useTranslation();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("token");

    axios
      .get(`${process.env.REACT_APP_APP_URL}/favorites`, {
        headers: {
          "Content-Type": "application/json",
          "X-Platform": process.env.REACT_APP_X_PLATFORM,
          "API-Key": process.env.REACT_APP_API_KEY,
          "X-API-Version": process.env.REACT_APP_X_API_VERSION,
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true,
      })
      .then((response) => {
        if (response.data.success) {
          const data = response.data.data.map((room) => ({
            ...room,
            isFavorite: true,
          }));
          setFavorites(data);
        } else {
          setError("Failed to fetch favorite rooms.");
        }
      })
      .catch((error) => {
        console.error("Error fetching favorites:", error);
        setError("Error fetching data. Please try again.");
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const handleCardClick = useCallback(
    (roomTypeId) => {
      navigate(`/detail-room/${roomTypeId}`);
    },
    [navigate]
  );

  const toggleFavorite = useCallback((e, roomTypeId) => {
    e.stopPropagation();
    const token = localStorage.getItem("token");

    axios
      .delete(`${process.env.REACT_APP_APP_URL}/favorites/${roomTypeId}`, {
        headers: {
          "Content-Type": "application/json",
          "X-Platform": process.env.REACT_APP_X_PLATFORM,
          "API-Key": process.env.REACT_APP_API_KEY,
          "X-API-Version": process.env.REACT_APP_X_API_VERSION,
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true,
      })
      .then((response) => {
        if (response.status === 200 && response.data.message) {
          Swal.fire({
            icon: "success",
            title: "Data favorite diperbaharui",
            text: response.data.message,
            timer: 1500,
            showConfirmButton: false,
          }).then(() => {
            window.location.reload();
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Failed to remove favorite.",
            timer: 1500,
            showConfirmButton: false,
          });
        }
      })
      .catch((error) => {
        console.error("Error removing favorite:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Error removing favorite. Please try again.",
          timer: 1500,
          showConfirmButton: false,
        });
      });
  }, []);

  if (loading) {
    return (
      <Container className="favorite-container">
        <Spinner
          animation="border"
          variant="primary"
          className="d-block mx-auto mt-4"
        />
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="favorite-container">
        <Alert variant="danger" className="favorite-alert">
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="favorite-container">
      <h2 className="favorite-title mb-4">My Favorite Rooms</h2>
      {favorites.length === 0 ? (
        <Alert variant="info" className="favorite-alert">
          You have no favorite rooms.
        </Alert>
      ) : (
        <Row className="favorite-grid">
          {favorites.map((room) => (
            <Col key={room.id} md={4} sm={12} className="mb-4">
              <Card
                className="favorite-card"
                onClick={() => handleCardClick(room.id)}
              >
                <div className="favorite-img-container">
                  {room.photos && room.photos.length > 0 ? (
                    <Card.Img
                      variant="top"
                      src={room.photos[0].photo_url}
                      alt={room.name}
                    />
                  ) : (
                    <Card.Img
                      variant="top"
                      src="/assets/placeholder.jpg"
                      alt="Placeholder"
                    />
                  )}
                  <Button
                    variant="link"
                    className="favorite-heart-btn"
                    onClick={(e) => toggleFavorite(e, room.id)}
                  >
                    <FaHeart
                      size={22}
                      className={room.isFavorite ? "text-danger" : "text-muted"}
                    />
                  </Button>
                </div>
                <Card.Body className="favorite-card-body">
                  <Card.Title className="favorite-card-title">
                    {room.name}
                  </Card.Title>
                  {room.rating && room.rating.average && (
                    <RatingStars ratingValue={room.rating.average} />
                  )}
                  <Card.Text className="favorite-card-text">
                    <p>
                      <strong>Capacity:</strong> {room.capacity} persons
                    </p>

                    <p>
                      <strong>Status:</strong> {room.status}
                    </p>
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </Container>
  );
}

export default Favorite;
